import { apiSlice } from "./apiSlice";
import {
  cancelPlannedOrders,
  confirmPlannedOrders,
  getAcquisitionCodes,
  getPlannedOrders,
  getPlannedOrdersProducts,
  getPlannedOrderStatus,
  getProjectedStocks,
} from "../slice/plannerSlice";

// RTK Query is source of truth for list refetch via tags; slice mirrors are best-effort UI cache.
const ignoreMirrorError = () => {};

export const plannerApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    cancelPlannedOrders: builder.mutation({
      query: data => ({
        url: `/api_order/planned_order/bulk_cancel/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (data, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(() => dispatch(cancelPlannedOrders(data))).catch(ignoreMirrorError);
      },
      invalidatesTags: ["plannedOrderList"],
    }),
    confirmPlannedOrders: builder.mutation({
      query: data => ({
        url: `/api_order/planned_order/bulk_confirm/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (data, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(() => dispatch(confirmPlannedOrders(data))).catch(ignoreMirrorError);
      },
      invalidatesTags: ["plannedOrderList"],
    }),
    createOrderFromPlannedOrder: builder.mutation({
      query: data => ({ url: `/api_order/create_order/`, method: "POST", body: data }),
      // Converted status comes from server refetch after invalidate; no optimistic slice mirror
      // so Draft convert stays consistent with BE.
      invalidatesTags: ["plannedOrderList"],
    }),
    getAcquisitionCodes: builder.query({
      query: params =>
        params ? `/api_order/acquisition_codes/?${params}` : "/api_order/acquisition_codes/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getAcquisitionCodes(result.data)))
          .catch(ignoreMirrorError);
      },
    }),
    getPlannedOrders: builder.query({
      query: params =>
        params ? `/api_order/planned_order/?${params}` : "/api_order/planned_order/",
      providesTags: ["plannedOrderList"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPlannedOrders(result.data)))
          .catch(ignoreMirrorError);
      },
    }),
    getPlannedOrdersProducts: builder.query({
      query: () => "/api_order/planned_orders_products/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPlannedOrdersProducts(result.data.data)))
          .catch(ignoreMirrorError);
      },
    }),
    getPlannedOrderStatus: builder.query({
      query: () => "/api_order/planned_order_status/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPlannedOrderStatus(result.data)))
          .catch(ignoreMirrorError);
      },
    }),
    getProjectedStocks: builder.query({
      query: params =>
        params ? `/api_product/projected_stocks/?${params}` : "/api_product/projected_stocks/",
      providesTags: ["projectedStockList"],
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getProjectedStocks(result.data)))
          .catch(ignoreMirrorError);
      },
    }),
    mutatePlannedOrder: builder.mutation({
      query: ({ id, ...rest }) => ({
        url: `/api_order/planned_order/${id}/`,
        method: "PATCH",
        body: rest,
      }),
    }),
    mutatePlannedOrderDetail: builder.mutation({
      query: data => ({
        url: "/api_order/planned_order/bulk_update/",
        method: "PATCH",
        body: data,
      }),
    }),
    refreshProjection: builder.mutation({
      query: () => ({ url: "/api_product/projection_refresh/", method: "POST" }),
      // Lists refresh when the Task Manager job completes (see TaskProvider).
    }),
  }),
});

export const {
  useCancelPlannedOrdersMutation,
  useConfirmPlannedOrdersMutation,
  useCreateOrderFromPlannedOrderMutation,
  useGetAcquisitionCodesQuery,
  useGetPlannedOrdersQuery,
  useGetPlannedOrdersProductsQuery,
  useGetPlannedOrderStatusQuery,
  useGetProjectedStocksQuery,
  useMutatePlannedOrderMutation,
  useMutatePlannedOrderDetailMutation,
  useRefreshProjectionMutation,
} = plannerApiSlice;
